import React, { useState, useContext, useEffect } from 'react';
import { DialogContent, Select, SelectOption } from '@indigo-ag/component-module';
import {
  AgFinancingDocumentType,
  AgFinancingFileFolder,
  AgFinancingFilesContext,
  AgFinancingFileStatus
} from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';
import i18next from 'core/i18n';

import {
  EnhancedDialog,
  UploadDocument,
  documentRequestTypesOptions,
  ModalActionButtons,
  phase3FileTypes,
  validatePhase2Documents
} from 'shared';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import {
  uploadFilesToAgFinancing,
  ProgressDictionary
} from 'features/brazil-grower/components/BrazilGrowerFinance/utils';
import {
  createAgFinancingFile,
  getDocumentsQueryByFinancingId,
  financingAgFinancing
} from 'features/brazil-grower/queries';
import { UserContext } from 'core/components/GateWay/GateWay';

interface UploadDocumentModalProps {
  closeModal: () => void;
  open: boolean;
}

export const UploadDocumentModal: React.FunctionComponent<UploadDocumentModalProps> = ({ closeModal, open }) => {
  /**
   * State
   */
  const [files, setFiles] = useState<File[] | null>(null);
  const [documentType, setDocumentType] = useState<AgFinancingDocumentType | null>(null);
  const [progress, setProgress] = useState<ProgressDictionary>();

  /**
   * Finance Process Context
   */
  const { agFinancingId, agFinance } = useContext(FinanceProcessContext);

  /**
   * Mutations
   */
  const [createAgFinancingFileFn] = useMutation(createAgFinancingFile);

  /**
   * User Context
   */
  const { isLuchesi, isSuper, isFlc, isCreditAnalyst, isCreditLead } = useContext(UserContext);
  useEffect(() => {
    setFiles(null);
    setDocumentType(null);
  }, [open]);

  const uploadFilesToPhase2 = uploadFilesToAgFinancing({
    agFinancingId,
    folder: AgFinancingFileFolder.PHASE_2
  });

  const updateDocumentType = (e: string | null) => {
    setDocumentType(e as AgFinancingDocumentType);
  };

  const updateFiles = (files: File[]) => {
    setFiles(files);
  };

  const uploadFile = () => {
    if (files && documentType) {
      return uploadFilesToPhase2(files, AgFinancingFilesContext.DOCUMENTS, documentType, percentCompleted =>
        setProgress({ ...percentCompleted })
      )
        .then(filesUploaded => {
          const filesUploadedWithAllInfo = filesUploaded.fulfilled.map(file => ({
            ...file,
            agFinancingId,
            status: AgFinancingFileStatus.SENT
          }));

          return createAgFinancingFileFn({
            refetchQueries: [
              {
                ...getDocumentsQueryByFinancingId(agFinancingId)
              },
              { query: financingAgFinancing, variables: { agFinancingId } }
            ],
            awaitRefetchQueries: documentType === AgFinancingDocumentType.MATRICULA,
            variables: {
              data: filesUploadedWithAllInfo
            }
          });
        })
        .then(() => {
          setFiles(null);
          setDocumentType(null);
          setProgress({});
        });
    } else {
      return Promise.reject();
    }
  };

  const renderOptions = (options: SelectOption[]) => {
    const canViewPhase3FileTypes = isLuchesi || isSuper || isCreditAnalyst || isCreditLead;

    options = validatePhase2Documents(options, agFinance);

    if (!canViewPhase3FileTypes) {
      options = options.filter(op => !phase3FileTypes.includes(op.value as AgFinancingDocumentType));
    }

    if (isLuchesi) {
      return options.filter(option => option.value !== AgFinancingDocumentType.INCOME_STATEMENT);
    }
    if (isFlc) {
      return options.filter(
        option =>
          option.value !== AgFinancingDocumentType.MATRICULA &&
          option.value !== AgFinancingDocumentType.CONSENT_LETTER &&
          option.value !== AgFinancingDocumentType.PROMISSORY_NOTE &&
          option.value !== AgFinancingDocumentType.CPR
      );
    }

    return options;
  };
  return (
    <EnhancedDialog open={open} onClose={closeModal} title={i18next.t('uploadDocument')} dialogMinHeight="280px">
      <DialogContent>
        <Select
          label={i18next.t('documentType')}
          name="documentType"
          options={renderOptions(documentRequestTypesOptions)}
          value={documentType}
          onSelectionChanged={updateDocumentType}
          placeholder={i18next.t('pleaseSelect')}
        />
        <UploadDocument files={files || []} setFiles={updateFiles} progress={progress} />
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('send')}
        closeModal={closeModal}
        disabled={!files?.length || !documentType}
        save={uploadFile}
      />
    </EnhancedDialog>
  );
};
